import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AIChat, AIFloatingButton } from '@/components/AIChat';
import { 
  Wallet, ShoppingBag, Building2, Car, TrendingUp, 
  PiggyBank, Landmark, ArrowRight, Plus, Send, Zap,
  ArrowUpRight, CheckCircle2, Sparkles, Target, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const verticalLinks = [
  { icon: ShoppingBag, name: 'Shop', href: '/buy', color: 'from-cyan-500 to-teal-500' },
  { icon: Building2, name: 'Homes', href: '/homes', color: 'from-green-500 to-emerald-500' },
  { icon: Car, name: 'Auto', href: '/auto', color: 'from-yellow-500 to-amber-500' },
  { icon: TrendingUp, name: 'Markets', href: '/markets', color: 'from-orange-500 to-red-500' },
  { icon: PiggyBank, name: 'Wealth', href: '/wealth', color: 'from-cyan-500 to-blue-500' },
  { icon: Landmark, name: 'Capital', href: '/capital', color: 'from-purple-500 to-pink-500' },
];

const formatCurrency = (amount, currency = 'KES') => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export default function DashboardPage() {
  const { user, token, refreshUser } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickInvestAmount, setQuickInvestAmount] = useState('');
  const [investing, setInvesting] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);

  // Quick Invest state
  const [quickInvestEnabled, setQuickInvestEnabled] = useState(true);
  const [roundUpTotal, setRoundUpTotal] = useState(1250);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickInvest = async () => {
    const amount = parseFloat(quickInvestAmount) || roundUpTotal;
    if (amount <= 0) return;
    
    setInvesting(true);
    // Simulate investment
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success(`Invested ${formatCurrency(amount)} into your AI portfolio!`);
    setQuickInvestAmount('');
    setRoundUpTotal(0);
    setInvesting(false);
  };

  return (
    <AppLayout title={`Welcome back${user?.name ? `, ${user.name}` : ''}!`}>
      <div className="p-4 lg:p-6 space-y-6">
        {/* Top Row: Wallet + Quick Invest */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Wallet Card */}
          <Card className="wallet-gradient text-white overflow-hidden" data-testid="wallet-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-white/80 text-sm mb-1">Total Balance</p>
                  {loading ? (
                    <Skeleton className="h-10 w-40 bg-white/20" />
                  ) : (
                    <p className="text-3xl font-bold tabular-nums">
                      {formatCurrency(dashboardData?.wallet_balance || 0)}
                    </p>
                  )}
                </div>
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Wallet className="h-6 w-6" />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Link to="/wallet" className="flex-1">
                  <Button variant="secondary" className="w-full rounded-full h-10" data-testid="add-money-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Money
                  </Button>
                </Link>
                <Button variant="secondary" className="rounded-full h-10 px-5" data-testid="send-money-btn">
                  <Send className="h-4 w-4 mr-1" />
                  Send
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Invest Card */}
          <Card className="invest-gradient border-0 overflow-hidden" data-testid="quick-invest-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-orange-500 flex items-center justify-center quick-invest-pulse">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Quick Invest</h3>
                    <p className="text-sm text-muted-foreground">Round-up spare change</p>
                  </div>
                </div>
                <Badge className="bg-green-500 text-white">+15.8%</Badge>
              </div>

              {roundUpTotal > 0 && (
                <div className="bg-white/50 rounded-xl p-3 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Round-ups ready</span>
                    <span className="font-bold text-green-600">{formatCurrency(roundUpTotal)}</span>
                  </div>
                  <Progress value={75} className="h-2" />
                  <p className="text-xs text-muted-foreground mt-1">From 12 purchases this week</p>
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder={`${formatCurrency(roundUpTotal)} ready`}
                  value={quickInvestAmount}
                  onChange={(e) => setQuickInvestAmount(e.target.value)}
                  className="flex-1 bg-white/50"
                  data-testid="quick-invest-input"
                />
                <Button 
                  onClick={handleQuickInvest}
                  disabled={investing}
                  className="rounded-full bg-gradient-to-r from-green-500 to-orange-500 text-white"
                  data-testid="invest-now-btn"
                >
                  {investing ? <Sparkles className="h-4 w-4 animate-spin" /> : 'Invest'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Portfolio Summary */}
        <Card data-testid="portfolio-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Your Portfolio</h3>
              <Link to="/wealth" className="text-sm text-cyan-600 hover:underline flex items-center">
                View details <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 rounded-xl bg-cyan-50">
                <p className="text-xs text-muted-foreground mb-1">Invested</p>
                <p className="font-bold text-lg tabular-nums">{formatCurrency(45000)}</p>
              </div>
              <div className="p-3 rounded-xl bg-green-50">
                <p className="text-xs text-muted-foreground mb-1">Returns</p>
                <p className="font-bold text-lg text-green-600 tabular-nums">+{formatCurrency(7110)}</p>
              </div>
              <div className="p-3 rounded-xl bg-orange-50">
                <p className="text-xs text-muted-foreground mb-1">Total Value</p>
                <p className="font-bold text-lg tabular-nums">{formatCurrency(52110)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Partner Program Banner */}
        <Card className="overflow-hidden border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30" data-testid="partner-banner">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                  <Landmark className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Jambo Capital Partner</h3>
                  <p className="text-sm text-muted-foreground">Earn by referring customers & reselling products</p>
                </div>
              </div>
              <Link to="/partner">
                <Button className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90">
                  Partner Dashboard <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <section>
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {verticalLinks.map((item) => (
              <Link key={item.name} to={item.href}>
                <Card className="card-hover border-0 bg-muted/50" data-testid={`quick-${item.name.toLowerCase()}`}>
                  <CardContent className="p-4 flex flex-col items-center gap-2">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-sm font-medium">{item.name}</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Products */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Continue Shopping</h2>
            <Link to="/buy" className="text-cyan-600 text-sm font-medium flex items-center gap-1">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {loading ? (
              Array(4).fill(0).map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="aspect-square" />
                  <CardContent className="p-3">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-5 w-1/2" />
                  </CardContent>
                </Card>
              ))
            ) : (
              dashboardData?.featured_products?.map((product) => (
                <Link key={product.id} to="/buy">
                  <Card className="overflow-hidden card-hover product-card" data-testid={`product-${product.id}`}>
                    <div className="aspect-square overflow-hidden bg-muted">
                      <img 
                        src={product.image_url || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400'}
                        alt={product.name}
                        className="product-image w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-3">
                      <p className="font-medium text-sm truncate">{product.name}</p>
                      <p className="text-cyan-600 font-bold price-tag">
                        {formatCurrency(product.price)}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))
            )}
          </div>
        </section>

        {/* Top Investments */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Top Investments</h2>
            <Link to="/markets" className="text-cyan-600 text-sm font-medium flex items-center gap-1">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {loading ? (
              Array(3).fill(0).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <Skeleton className="w-12 h-12 rounded-xl" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-1/3 mb-2" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                    <Skeleton className="h-6 w-16" />
                  </CardContent>
                </Card>
              ))
            ) : (
              dashboardData?.featured_investments?.map((investment) => (
                <Link key={investment.id} to="/markets">
                  <Card className="card-hover investment-gradient" data-testid={`investment-${investment.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-green-500 flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{investment.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{investment.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-500 font-bold">+{investment.return_rate}%</p>
                        <p className="text-xs text-muted-foreground">Return</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))
            )}
          </div>
        </section>

        {/* Recent Activity */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recent Activity</h2>
            <Link to="/wallet" className="text-cyan-600 text-sm font-medium flex items-center gap-1">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 space-y-4">
                  {Array(3).fill(0).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-1/3 mb-1" />
                        <Skeleton className="h-3 w-1/4" />
                      </div>
                      <Skeleton className="h-5 w-20" />
                    </div>
                  ))}
                </div>
              ) : dashboardData?.recent_transactions?.length ? (
                <div className="divide-y">
                  {dashboardData.recent_transactions.map((tx) => (
                    <div key={tx.id} className="p-4 flex items-center gap-3" data-testid={`tx-${tx.id}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        tx.type === 'topup' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'
                      }`}>
                        {tx.type === 'topup' ? <Plus className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium capitalize">{tx.type}</p>
                        <p className="text-xs text-muted-foreground truncate">{tx.description}</p>
                      </div>
                      <p className={`font-bold ${tx.type === 'topup' ? 'text-green-600' : 'text-orange-600'}`}>
                        {tx.type === 'topup' ? '+' : '-'}{formatCurrency(tx.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No recent transactions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </div>

      {/* AI Chat */}
      <AIFloatingButton onClick={() => setShowAIChat(true)} />
      <AIChat isOpen={showAIChat} onClose={() => setShowAIChat(false)} />
    </AppLayout>
  );
}
